import { useEffect } from 'react'
import {
  cancelledDisplay,
  missedDisplay,
  reasonConfig,
  rejectedDisplay,
} from '../config/callReasons'
import type { RecordingStatus } from '../types'
import { RecordingStatusBadge } from './RecordingStatusBadge'

interface Props {
  open: boolean
  onClose: () => void
}

interface RecordingEntry {
  status: RecordingStatus
  description: string
}

const RECORDING_STATUSES: RecordingEntry[] = [
  {
    status: 'ready',
    description:
      'Запись готова: relay успешно склеил две дорожки (микрофон оператора + динамик собеседника) в один .ogg файл. Можно слушать и скачать.',
  },
  {
    status: 'partial_sim',
    description:
      'Сохранена только sim-дорожка (callee). Микрофон оператора в этом звонке молчал (закрыт/выключен браузер у оператора) — relay не стал стучаться в ffmpeg с пустым входом и сохранил уцелевшую сторону отдельно.',
  },
  {
    status: 'partial_op',
    description:
      'Сохранена только operator-дорожка. Callee молчал или его аудио не дошло до relay. Запись играется одной стороной — оператор слышен, абонент нет.',
  },
  {
    status: 'incomplete',
    description:
      'Звонок был переподключён (F5 / wifi-cell свитч), и relay не успел дописать поздний фрагмент в общий файл (concat задержался > 10 минут). Запись играется — основной разговор сохранён, потерян только хвост. На рестарте relay попробует дописать ещё раз, и при успехе статус сменится на «Готово».',
  },
  {
    status: 'merge_failed',
    description:
      'Relay не смог склеить аудио — файл прослушать нельзя. Исходные фрагменты переименованы в `.failed-<unix_ts>` и автоматически удалятся через 30 дней (см. pruneFailedRaws). На следующем рестарте relay не пытается их повторно склеить, чтобы не зацикливаться на одной и той же ошибке.',
  },
  {
    status: 'deleted',
    description: 'Запись удалена админом с диска. Восстановить нельзя.',
  },
]

interface ReasonEntry {
  /** Stable key for React. */
  key: string
  /** A representative raw reason that maps to the displayed badge label. Used to read color/bg from reasonConfig so the badge looks identical to what admins see in tables. Null for synthetic display labels (e.g. Rejected) that don't have a 1:1 raw reason. */
  sample: keyof typeof reasonConfig | null
  /** Optional override badge — used for synthetic labels not present in reasonConfig. */
  override?: { label: string; color: string; bg: string }
  description: string
}

const CALL_REASONS: ReasonEntry[] = [
  {
    key: 'normal',
    sample: 'normal',
    description: 'Звонок прошёл успешно, обе стороны общались.',
  },
  {
    key: 'operator-rejected',
    sample: null,
    override: rejectedDisplay,
    description: 'Входящий звонок: оператор сбросил вызов до того как ответить. Длительность 0 секунд, аудио не записано. Это действие оператора, не сбой.',
  },
  {
    key: 'operator-cancelled',
    sample: null,
    override: cancelledDisplay,
    description: 'Исходящий звонок: оператор отменил набор до того как абонент ответил. Длительность 0 секунд, аудио не записано. Это действие оператора, не сбой.',
  },
  {
    key: 'busy',
    sample: 'busy',
    description:
      'Линия занята. Три варианта: SIM собеседника вернула busy tone; оператор уже в другом звонке на этой же трубке; трубка уже занята другим оператором.',
  },
  {
    key: 'callee-rejected',
    sample: 'rejected',
    description:
      'Абонент явно отклонил вызов на своей стороне (нажал «отбой» / стоит блокировка номера). Это реакция callee, не оператора — отличается от «Сброс менеджером» выше, который означает «наш оператор сам нажал отбой».',
  },
  {
    key: 'restricted',
    sample: 'restricted',
    description:
      'Оператор сотовой связи ограничил вызов: блокировка номера, исчерпан баланс/лимит, роуминг недоступен. Звонок не дошёл до абонента.',
  },
  {
    key: 'answered_elsewhere',
    sample: 'answered_elsewhere',
    description:
      'Входящий звонок забрало другое устройство абонента (другая трубка, умные часы). На нашей SIM не сняли, но звонок не пропал — абонент ответил где-то ещё.',
  },
  {
    key: 'no_answer',
    sample: 'no_answer',
    description:
      'Исходящий звонок: оператор набрал, но абонент не снял — либо SIM сообщила «не отвечает», либо звонок прозвонил больше 30 секунд. Недозвон оператора.',
  },
  {
    key: 'incoming-missed',
    sample: null,
    override: missedDisplay,
    description:
      'Входящий звонок: на SIM позвонили, но оператор не снял трубку (или звонок сорвался до ответа). Это пропущенный входящий — не действие оператора и не сбой телефона, поэтому на хитмапе такую трубку он не краснит.',
  },
  {
    key: 'remote_hangup',
    sample: 'remote_hangup',
    description: 'Собеседник сам положил трубку. Нормальное завершение со стороны абонента.',
  },
  {
    key: 'no_sim',
    sample: 'no_sim',
    description:
      'На телефоне нет рабочей SIM в момент набора — pre-flight проверка остановила звонок до того как он ушёл в линию. Сигнал админу: разобраться с телефоном.',
  },
  {
    key: 'no_network',
    sample: 'no_network',
    description:
      'Телефон без сотовой сети в момент набора (нет покрытия, SIM не зарегистрирована). Pre-flight завершил вызов до набора. Сигнал админу.',
  },
  {
    key: 'agent_lost_call',
    sample: 'agent_lost_call',
    description:
      'Агент на телефоне потерял синхронизацию с relay — обычно после переподключения телефона к серверу. Звонок принудительно завершён.',
  },
  {
    key: 'sync_timeout',
    sample: 'sync_timeout',
    description:
      'Превышено время ожидания внутреннего этапа: либо синхронизация при переподключении агента, либо застряло промежуточное состояние звонка.',
  },
  {
    key: 'webrtc_failed',
    sample: 'webrtc_failed',
    description:
      'Не удалось установить или удержать аудио-канал WebRTC. Либо setup не дошёл до конца, либо канал упал во время разговора. Часто это сетевые проблемы у браузера или у телефона.',
  },
  {
    key: 'agent_disconnected',
    sample: 'agent_disconnected',
    description:
      'Кто-то из участников потерял WebSocket: телефон ушёл в офлайн, браузер закрылся или начал переподключаться, и grace-period (5–15 сек) истёк.',
  },
  {
    key: 'browser_replaced',
    sample: 'browser_replaced',
    description:
      'Оператор открыл UI в другой вкладке/устройстве — старый сеанс оборвался автоматически, новый перехватил звонок.',
  },
  {
    key: 'phone_revoked',
    sample: 'phone_revoked',
    description:
      'Админ отозвал/деактивировал телефон или отвязал его от ключа доступа во время звонка. Звонок принудительно завершён системой — это admin-action, а не сбой.',
  },
  {
    key: 'operator_revoked',
    sample: 'operator_revoked',
    description:
      'Админ отозвал доступ оператора во время звонка (отвязка ключа, переназначение телефона другому, удаление оператора). Звонок принудительно завершён.',
  },
  {
    key: 'error',
    sample: 'error',
    description:
      'Ошибка без конкретной классификации. Fallback для редких случаев — нужно смотреть логи relay/backend.',
  },
]

function CallReasonBadge({
  config,
}: {
  config: { label: string; color: string; bg: string }
}) {
  return (
    <span
      className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${config.color} ${config.bg}`}
    >
      {config.label}
    </span>
  )
}

export function StatusesHelpModal({ open, onClose }: Props) {
  useEffect(() => {
    if (!open) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl my-4 md:my-0 max-h-[calc(100vh-2rem)] flex flex-col"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="statuses-help-title"
      >
        <div className="px-6 pt-6 pb-3 flex items-start justify-between border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div>
            <h2
              id="statuses-help-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Что значат статусы
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Расшифровка бейджей в таблицах звонков и записей
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1 -mt-1 -mr-1"
            aria-label="Закрыть"
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-8">
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-3">
              Статусы записи
            </h3>
            <ul className="space-y-3">
              {RECORDING_STATUSES.map(entry => (
                <li key={entry.status} className="flex gap-3 items-start">
                  <div className="shrink-0 w-32 pt-0.5">
                    <RecordingStatusBadge status={entry.status} />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                    {entry.description}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-3">
              Результат звонка
            </h3>
            <ul className="space-y-3">
              {CALL_REASONS.map(entry => {
                const config = entry.override ?? (entry.sample !== null ? reasonConfig[entry.sample] : null)
                if (!config) return null
                return (
                  <li key={entry.key} className="flex gap-3 items-start">
                    <div className="shrink-0 w-32 pt-0.5">
                      <CallReasonBadge config={config} />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                      {entry.description}
                    </p>
                  </li>
                )
              })}
            </ul>
          </section>
        </div>
      </div>
    </div>
  )
}

interface HelpButtonProps {
  onClick: () => void
}

export function HelpButton({ onClick }: HelpButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center w-7 h-7 rounded-full text-blue-600 hover:bg-blue-100 hover:text-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/40 dark:hover:text-blue-200 transition-colors"
      aria-label="Что значат статусы"
      title="Что значат статусы"
    >
      <svg
        className="w-6 h-6"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </button>
  )
}
